import { http } from '@/api/client/httpClient';
import type { PagedResult } from '@/api/types/abp';
import type { Select2Page } from '@/components/forms/Select2Async';
import type {
  ApplySwapFreeTargetChangesInput,
  CreateSwapFreeConfigurationFromTargetsInput,
  DeleteSwapFreeConfigurationInput,
  GetConfigurationsInput,
  GetConfigurationTargetsInput,
  GetGroupTargetsInput,
  GetHistoryDealsInput,
  GetLoginsByGroupsInput,
  GetLoginTargetsInput,
  HistoryDealDto,
  ResultDto,
  SetSwapFreeConfigurationStatusInput,
  SwapFreeConfigurationDto,
  SwapFreeConfigurationTargetDto,
  SwapFreeOptionsDto,
  SwapFreeTargetDto,
  UpdateSwapFreeConfigurationScalarsInput,
} from '@/api/types/swapFreeControl';

const BASE = '/api/app/swap-free-configuration';

interface PaginatedParams {
  search?: string;
  skipCount: number;
  maxResultCount: number;
}

export const swapFreeControlApi = {
  getOptions: () => http.get<SwapFreeOptionsDto>(`${BASE}/options`).then((r) => r.data),

  getConfigurations: (input: GetConfigurationsInput) =>
    http.get<PagedResult<SwapFreeConfigurationDto>>(`${BASE}/configurations`, { params: input }).then((r) => r.data),

  getConfigurationTargets: (input: GetConfigurationTargetsInput) =>
    http.get<PagedResult<SwapFreeConfigurationTargetDto>>(`${BASE}/configuration-targets`, { params: input }).then((r) => r.data),

  createFromTargets: (input: CreateSwapFreeConfigurationFromTargetsInput) =>
    http.post<void>(`${BASE}/create-from-targets`, input).then((r) => r.data),

  updateScalars: (input: UpdateSwapFreeConfigurationScalarsInput) =>
    http.post<void>(`${BASE}/update-scalars`, input).then((r) => r.data),

  applyTargetChanges: (input: ApplySwapFreeTargetChangesInput) =>
    http.post<void>(`${BASE}/apply-target-changes`, input).then((r) => r.data),

  setStatus: (input: SetSwapFreeConfigurationStatusInput) =>
    http.post<void>(`${BASE}/set-status`, input).then((r) => r.data),

  delete: (input: DeleteSwapFreeConfigurationInput) =>
    http.post<void>(`${BASE}/delete`, input).then((r) => r.data),

  getHistoryDeals: (input: GetHistoryDealsInput) =>
    http.get<PagedResult<HistoryDealDto>>(`${BASE}/history-deals`, { params: input }).then((r) => r.data),

  getGroupTargets: (input: GetGroupTargetsInput) =>
    http.get<PagedResult<SwapFreeTargetDto>>(`${BASE}/group-targets`, { params: input }).then((r) => r.data),

  getLoginTargets: (input: GetLoginTargetsInput) =>
    http.get<PagedResult<SwapFreeTargetDto>>(`${BASE}/login-targets`, { params: input }).then((r) => r.data),

  getConfigurationNames: (input: PaginatedParams) =>
    http.get<PagedResult<ResultDto<string>>>(`${BASE}/configuration-names`, { params: input }).then((r) => r.data),

  getTradingGroups: (input: PaginatedParams) =>
    http.get<PagedResult<ResultDto<string>>>(`${BASE}/trading-groups`, { params: input }).then((r) => r.data),

  getTradingLogins: (input: PaginatedParams) =>
    http.get<PagedResult<ResultDto<number>>>(`${BASE}/trading-logins`, { params: input }).then((r) => r.data),

  getLoginsByGroups: (input: GetLoginsByGroupsInput) =>
    http.get<PagedResult<ResultDto<number>>>(`${BASE}/logins-by-groups`, { params: input }).then((r) => r.data),
};

const PAGE_SIZE = 20;

// --- Paginated select2 fetchers (term, page) -> {items, hasMore} -------------

export function fetchConfigurationNames(term: string, page: number): Promise<Select2Page> {
  return swapFreeControlApi
    .getConfigurationNames({ search: term || undefined, skipCount: (page - 1) * PAGE_SIZE, maxResultCount: PAGE_SIZE })
    .then((res) => ({
      items: res.items.map((i) => ({ value: i.value, text: i.text })),
      hasMore: (page - 1) * PAGE_SIZE + res.items.length < res.totalCount,
    }));
}

export function fetchTradingGroups(term: string, page: number): Promise<Select2Page> {
  return swapFreeControlApi
    .getTradingGroups({ search: term || undefined, skipCount: (page - 1) * PAGE_SIZE, maxResultCount: PAGE_SIZE })
    .then((res) => ({
      items: res.items.map((i) => ({ value: i.value, text: i.text })),
      hasMore: (page - 1) * PAGE_SIZE + res.items.length < res.totalCount,
    }));
}

export function fetchTradingLogins(term: string, page: number): Promise<Select2Page> {
  return swapFreeControlApi
    .getTradingLogins({ search: term || undefined, skipCount: (page - 1) * PAGE_SIZE, maxResultCount: PAGE_SIZE })
    .then((res) => ({
      items: res.items.map((i) => ({ value: String(i.value), text: i.text })),
      hasMore: (page - 1) * PAGE_SIZE + res.items.length < res.totalCount,
    }));
}

export function makeLoginsByGroupsFetcher(groups: string[]) {
  return (term: string, page: number): Promise<Select2Page> =>
    swapFreeControlApi
      .getLoginsByGroups({ groups, search: term || undefined, skipCount: (page - 1) * PAGE_SIZE, maxResultCount: PAGE_SIZE })
      .then((res) => ({
        items: res.items.map((i) => ({ value: String(i.value), text: i.text })),
        hasMore: (page - 1) * PAGE_SIZE + res.items.length < res.totalCount,
      }));
}
